<template>
  <div class="events-chart-container">
    <div class="chart-header">
      <h3 class="chart-title">{{ $ml.get('temporal_distribution_events') }}</h3>
      <div class="chart-controls">
        <v-select
          v-model="selectedTimeUnit"
          :items="timeUnits"
          :label="$ml.get('period')"
          :key="`timeunit-${$ml.current}`"
          dense
          outlined
          hide-details
          class="time-unit-select"
        ></v-select>
      </div>
    </div>
    
    <div class="chart-wrapper">
      <canvas ref="chartCanvas" class="chart-canvas" :style="{ display: hasData ? 'block' : 'none' }"></canvas>
      <div v-if="!hasData" class="no-data-message">
        <v-icon large color="grey lighten-1">mdi-chart-line</v-icon>
        <p class="text-body-2 text-grey mt-2">
          {{ $ml.get('no_data_events') }}
        </p>
      </div>
    </div>
    
    <div class="chart-legend">
      <div class="legend-item" v-for="eventType in allEventTypes" :key="eventType.name">
        <div 
          class="legend-color" 
          :style="{ backgroundColor: eventType.color }"
        ></div>
        <span class="legend-label">{{ eventType.name }}</span>
        <span class="legend-count">({{ getEventCount(eventType.name) }})</span>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js';

export default {
  name: 'EventsChart',
  props: {
    events: {
      type: Array,
      default: () => []
    },
    timeRange: {
      type: Object,
      default: () => ({ start: null, end: null })
    }
  },
  data() {
    return {
      chart: null,
      selectedTimeUnit: 'hour',
      eventTypes: [
        { name: 'LPR', color: '#45B7D1', key: 'lpr' },
        { name: 'Recordings', color: '#96CEB4', key: 'recording' }
      ],
      smartEventTypes: [], // Se genera dinámicamente basado en el contenido
      alertTypes: [], // Se genera dinámicamente basado en el contenido de alertas
      objectTypes: [] // Se genera dinámicamente basado en el contenido de objetos
    };
  },
  computed: {
    hasData() {
      return this.events && this.events.length > 0;
    },
    allEventTypes() {
      return [...this.eventTypes, ...this.smartEventTypes, ...this.alertTypes, ...this.objectTypes];
    },
    timeUnits() {
      return [
        { text: this.$ml.get('by_hour'), value: 'hour' },
        { text: this.$ml.get('by_minute'), value: 'minute' }
      ];
    }
  },
  watch: {
    events: {
      handler(newEvents) {
        if (newEvents && newEvents.length > 0) {
          this.updateChart();
        }
      },
      deep: true,
      immediate: true
    },
    selectedTimeUnit() {
      this.updateChart();
    },
    '$ml.current': function() {
      // Detectar cambio de idioma y actualizar el gráfico
      this.updateChartLabelsOnLanguageChange();
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.createChart();
    });
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
  },
  methods: {
    createChart() {
      if (!this.$refs.chartCanvas) {
        console.warn('Chart canvas not available yet');
        return;
      }
      
      const ctx = this.$refs.chartCanvas.getContext('2d');
      
      this.chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: [],
          datasets: [] // Se inicializa vacío, se llena en updateChart
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: false // Usamos nuestra propia leyenda
          },
          backgroundColor: '#2C2C2C',
          tooltips: {
            backgroundColor: '#424242',
            titleFontColor: '#ffffff',
            bodyFontColor: '#ffffff',
            borderColor: '#666666',
            borderWidth: 1,
            callbacks: {
              title: (tooltipItem, data) => {
                const label = data.labels[tooltipItem[0].index];
                // El punto representa el final del período, así que calcular el inicio
                const startLabel = this.getStartTimeLabel(label);
                return `${startLabel} - ${label}`;
              },
              label: (tooltipItem, data) => {
                const eventType = this.allEventTypes[tooltipItem.datasetIndex];
                if (!eventType) {
                  return `${this.$ml.get('event')}: ${tooltipItem.yLabel}`;
                }
                return `${eventType.name}, ${tooltipItem.yLabel}`;
              }
            }
          },
          scales: {
            xAxes: [{
              display: true,
              scaleLabel: {
                display: true,
                labelString: this.getTimeUnitLabel(),
                fontColor: '#ffffff'
              },
              ticks: {
                fontColor: '#ffffff'
              },
              gridLines: {
                color: 'rgba(255,255,255,0.1)'
              }
            }],
            yAxes: [{
              display: true,
              scaleLabel: {
                display: true,
                labelString: this.$ml.get('number_events'),
                fontColor: '#ffffff'
              },
              ticks: {
                beginAtZero: true,
                stepSize: 1,
                fontColor: '#ffffff'
              },
              gridLines: {
                color: 'rgba(255,255,255,0.1)'
              }
            }]
          }
        }
      });
      
      this.updateChart();
    },
    
    updateChart() {
      if (!this.chart) return;
      
      const chartData = this.processEventsData();
      
      this.chart.data.labels = chartData.labels;
      
      // Recrear datasets para todos los tipos de eventos
      this.chart.data.datasets = this.allEventTypes.map(eventType => ({
        label: eventType.name,
        data: chartData.data[eventType.key] || [],
        borderColor: eventType.color,
        backgroundColor: eventType.color + '20',
        tension: 0.4,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 6
      }));
      
      this.chart.options.scales.xAxes[0].scaleLabel.labelString = this.getTimeUnitLabel();
      this.chart.update();
    },
    
    processEventsData() {
      
      if (!this.events || this.events.length === 0) {
        
        return { labels: [], data: {} };
      }
      
      // Generar tipos de Smart Events, Alerts y Objects dinámicamente
      this.generateSmartEventTypes();
      this.generateAlertTypes();
      this.generateObjectTypes();
      
      
      
      // Agrupar eventos por tipo y período de tiempo
      const groupedData = {};
      this.allEventTypes.forEach(eventType => {
        groupedData[eventType.key] = {};
      });
      
      // Procesar cada evento/bucket
      this.events.forEach(event => {
        const eventType = this.getEventType(event);
        if (!eventType) return;
        
        if (!groupedData[eventType]) {
          groupedData[eventType] = {};
        }
        
        // Obtener la cantidad de eventos del bucket
        const eventCount = this.getEventCountFromBucket(event);
        
        // Si es un bucket con start y end, distribuir eventos proporcionalmente entre períodos
        if (event.metadata && (event.metadata.isBucket || event.metadata.eventCount) && event.start && event.end) {
          this.distributeBucketEvents(event, eventType, eventCount, groupedData);
        } else {
          // Para eventos individuales, usar el start para agrupar
          const timeKey = this.getTimeKey(event.start || event.timestamp);
          if (timeKey) {
            groupedData[eventType][timeKey] = (groupedData[eventType][timeKey] || 0) + eventCount;
          }
        }
      });
      
      // Crear etiquetas de tiempo y datos
      const allTimeKeys = new Set();
      Object.values(groupedData).forEach(data => {
        Object.keys(data).forEach(key => allTimeKeys.add(key));
      });
      
      const sortedTimeKeys = Array.from(allTimeKeys).sort();
      const labels = sortedTimeKeys.map(key => this.formatTimeLabel(key));
      
      // Llenar datos para cada tipo de evento
      const data = {};
      this.allEventTypes.forEach(eventType => {
        data[eventType.key] = sortedTimeKeys.map(key => 
          (groupedData[eventType.key] && groupedData[eventType.key][key]) || 0
        );
      });
      
      return { labels, data };
    },

    generateSmartEventTypes() {
      if (!this.events || this.events.length === 0) {
        this.smartEventTypes = [];
        return;
      }

      // Recopilar todos los contenidos únicos de Smart Events
      const smartEventContents = new Set();
      
      this.events.forEach(event => {
        if ((event.metadata && event.metadata.isSmartEvent) || event.group === 4 || 
            (event.className && event.className.includes('smart-event'))) {
          
          let content = '';
          // Priorizar metadata para obtener el tipo base del evento
          if (event.metadata && event.metadata.smartEvent) {
            const category = event.metadata.smartEvent.category || event.metadata.category || '';
            const title = event.metadata.smartEvent.title || '';
            if (category && title) {
              content = `${category} ${title}`.trim();
            } else if (category) {
              content = category.trim();
            } else if (title) {
              content = title.trim();
            }
          } else if (event.metadata && event.metadata.category) {
            content = event.metadata.category.trim();
          } else if (event.content) {
            content = event.content.trim();
          } else {
            content = this.$ml.get('smart_event');
          }
          
          // Normalizar el contenido: remover IDs de bucket y conteos
          content = this.normalizeEventContent(content);
          
          if (content) {
            smartEventContents.add(content);
          }
        }
      });

      // Generar colores dinámicamente
      const colors = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3', 
        '#54A0FF', '#5F27CD', '#00D2D3', '#FF9F43', '#10AC84', '#EE5A24',
        '#0984E3', '#A29BFE', '#6C5CE7', '#FD79A8', '#E17055', '#00B894'
      ];

      // Crear tipos de eventos para cada contenido único
      this.smartEventTypes = Array.from(smartEventContents).map((content, index) => ({
        name: content,
        color: colors[index % colors.length],
        key: `smartEvent_${content.replace(/\s+/g, '_').toLowerCase()}`
      }));
    },

    generateAlertTypes() {
      if (!this.events || this.events.length === 0) {
        this.alertTypes = [];
        return;
      }

      // Recopilar todos los contenidos únicos de Alerts (excluyendo objetos)
      const alertContents = new Set();
      
      this.events.forEach(event => {
        // Excluir objetos que también tienen group === 1
        if (event.metadata && event.metadata.isObject) {
          return; // Saltar objetos
        }
        
        if ((event.metadata && event.metadata.isAlert) || 
            (event.group === 1 && (!event.metadata || !event.metadata.isObject)) || 
            (event.className && event.className.includes('alert') && !event.className.includes('object'))) {
          
          let content = '';
          
          if (event.metadata && event.metadata.alert && event.metadata.alert.category) {
            content = event.metadata.alert.category.trim();
          } else if (event.content) {
            content = event.content.trim();
          } else if (event.metadata && event.metadata.alert && event.metadata.alert.type) {
            content = event.metadata.alert.type.trim();
          } else if (event.metadata && event.metadata.alertType) {
            content = event.metadata.alertType.trim();
          } else if (event.metadata && event.metadata.recordingType) {
            content = event.metadata.recordingType.trim();
          } else {
            content = this.$ml.get('alert');
          }
          
          if (content) {
            alertContents.add(content);
          }
        }
      });

      // Generar colores dinámicamente para alertas (usando tonos diferentes)
      const alertColors = [
        '#4ECDC4', '#26D0CE', '#1DD1A1', '#00D2D3', '#00A085', '#00B894',
        '#55A3FF', '#3742FA', '#2F3542', '#40407A', '#706FD3', '#7158E2',
        '#FF3838', '#FF4757', '#FF6B6B', '#FF7675', '#FDA7DF', '#FD79A8'
      ];

      // Crear tipos de eventos para cada contenido único de alerta
      this.alertTypes = Array.from(alertContents).map((content, index) => ({
        name: content,
        color: alertColors[index % alertColors.length],
        key: `alert_${content.replace(/\s+/g, '_').toLowerCase()}`
      }));
    },
    
    generateObjectTypes() {
      if (!this.events || this.events.length === 0) {
        this.objectTypes = [];
        return;
      }

      // Recopilar todos los contenidos únicos de Objects
      const objectContents = new Set();
      
      this.events.forEach(event => {
        if ((event.metadata && event.metadata.isObject) ||
            (event.className && event.className.includes('object-item'))) {
          
          let content = '';
          
          if (event.metadata && event.metadata.objectType) {
            content = event.metadata.objectType.trim();
          } else if (event.content) {
            // Extraer el tipo del contenido (ej: "🎯 carrot" -> "carrot")
            content = event.content.replace(/🎯\s*/, '').trim();
          } else if (event.metadata && event.metadata.event && event.metadata.event.title) {
            content = event.metadata.event.title.trim();
          } else {
            content = this.$ml.get('object') || 'Object';
          }
          
          if (content) {
            objectContents.add(content);
          }
        }
      });

      // Generar colores dinámicamente para objetos (usando tonos diferentes)
      const objectColors = [
        '#FFA500', '#FF8C00', '#FF7F50', '#FF6347', '#FF4500', '#FF1493',
        '#DA70D6', '#BA55D3', '#9370DB', '#8A2BE2', '#7B68EE', '#6A5ACD',
        '#FF69B4', '#FF1493', '#DC143C', '#C71585', '#DB7093', '#FFB6C1'
      ];

      // Crear tipos de eventos para cada contenido único de objeto
      this.objectTypes = Array.from(objectContents).map((content, index) => ({
        name: content,
        color: objectColors[index % objectColors.length],
        key: `object_${content.replace(/\s+/g, '_').toLowerCase()}`
      }));
    },
    
    getObjectContentKey(event) {
      // Obtener el contenido del Object
      let content = '';
      
      if (event.metadata && event.metadata.objectType) {
        content = event.metadata.objectType.trim();
      } else if (event.content) {
        // Extraer el tipo del contenido (ej: "🎯 carrot" -> "carrot")
        content = event.content.replace(/🎯\s*/, '').trim();
      } else if (event.metadata && event.metadata.event && event.metadata.event.title) {
        content = event.metadata.event.title.trim();
      } else {
        content = this.$ml.get('object') || 'Object';
      }
      
      // Crear una key única basada en el contenido
      return `object_${content.replace(/\s+/g, '_').toLowerCase()}`;
    },
    
    getEventType(event) {
      // Determinar el tipo de evento basado en sus propiedades
      // PRIORIDAD: Verificar metadata primero
      if (event.metadata) {
        if (event.metadata.isObject) {
          return this.getObjectContentKey(event);
        }
        if (event.metadata.isSmartEvent) {
          return this.getSmartEventContentKey(event);
        }
        if (event.metadata.isAlert) {
          return this.getAlertContentKey(event);
        }
        if (event.metadata.isLPR) {
          return 'lpr';
        }
        if (event.metadata.isRecording) {
          return 'recording';
        }
      }
      
      // Fallback: Verificar por group
      if (event.group === 1 && event.metadata && event.metadata.isObject) {
        // Objects tienen group 1, pero se distinguen por isObject
        return this.getObjectContentKey(event);
      }
      if (event.group === 1 && (!event.metadata || !event.metadata.isObject)) {
        // Alerts también tienen group 1, pero sin isObject
        return this.getAlertContentKey(event);
      }
      if (event.group === 4) {
        return this.getSmartEventContentKey(event);
      }
      if (event.group === 0 || event.group === 2) {
        return 'lpr';
      }
      if (event.group === 3) {
        return 'recording';
      }
      
      // Fallback basado en className
      if (event.className) {
        if (event.className.includes('object-item')) {
          return this.getObjectContentKey(event);
        }
        if (event.className.includes('smart-event')) {
          return this.getSmartEventContentKey(event);
        }
        if (event.className.includes('alert')) {
          return this.getAlertContentKey(event);
        }
        if (event.className.includes('lpr')) return 'lpr';
        if (event.className.includes('recording')) return 'recording';
      }
      
      return null;
    },

    getSmartEventContentKey(event) {
      // Obtener el contenido del Smart Event
      let content = '';
      
      // Priorizar metadata para obtener el tipo base del evento
      if (event.metadata && event.metadata.smartEvent) {
        const category = event.metadata.smartEvent.category || event.metadata.category || '';
        const title = event.metadata.smartEvent.title || '';
        if (category && title) {
          content = `${category} ${title}`.trim();
        } else if (category) {
          content = category.trim();
        } else if (title) {
          content = title.trim();
        }
      } else if (event.metadata && event.metadata.category) {
        content = event.metadata.category.trim();
      } else if (event.content) {
        content = event.content.trim();
      } else {
        content = this.$ml.get('smart_event');
      }
      
      // Normalizar el contenido: remover IDs de bucket y conteos
      content = this.normalizeEventContent(content);
      
      // Crear una key única basada en el contenido normalizado
      return `smartEvent_${content.replace(/\s+/g, '_').toLowerCase()}`;
    },
    
    normalizeEventContent(content) {
      if (!content) return '';
      
      content = content.trim();
      
      // Remover todos los patrones de paréntesis con números (IDs de bucket y conteos)
      // Ejemplos: "(138)", "(276)", "(138) (276)", etc.
      // Esto maneja casos como "smart-carrot carrot (138) (276)" -> "smart-carrot carrot"
      // O "smart-carrot carro (138) (276)" -> "smart-carrot carro"
      content = content.replace(/\s*\(\d+\)(\s*\(\d+\))*/g, '');
      
      // Normalizar espacios múltiples a uno solo
      content = content.replace(/\s+/g, ' ').trim();
      
      // Normalizar variaciones comunes: "carrot" y "carro" deberían tratarse como el mismo tipo
      // Normalizar "carrot" a "carro" para mantener consistencia con lo que el usuario espera
      // El usuario mencionó "smart-carrot carro", así que normalizamos "carrot" -> "carro"
      content = content.replace(/\bcarrot\b/gi, 'carro');
      
      return content.trim();
    },

    getAlertContentKey(event) {
      // Obtener el contenido de la Alerta - priorizar alert.category
      let content = '';
      
      if (event.metadata && event.metadata.alert && event.metadata.alert.category) {
        content = event.metadata.alert.category.trim();
      } else if (event.content) {
        content = event.content.trim();
      } else if (event.metadata && event.metadata.alert && event.metadata.alert.type) {
        content = event.metadata.alert.type.trim();
      } else if (event.metadata && event.metadata.alertType) {
        content = event.metadata.alertType.trim();
      } else if (event.metadata && event.metadata.recordingType) {
        content = event.metadata.recordingType.trim();
      } else {
        content = this.$ml.get('alert');
      }
      
      // Crear una key única basada en el contenido
      return `alert_${content.replace(/\s+/g, '_').toLowerCase()}`;
    },
    
    getTimeKey(timestamp) {
      if (!timestamp) return null;
      
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return null;
      
      switch (this.selectedTimeUnit) {
        case 'hour':
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}-${String(date.getHours()).padStart(2, '0')}`;
        case 'minute':
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}-${String(date.getHours()).padStart(2, '0')}-${String(date.getMinutes()).padStart(2, '0')}`;
        default:
          return null;
      }
    },
    
    formatTimeLabel(timeKey) {
      if (!timeKey) return '';
      
      switch (this.selectedTimeUnit) {
        case 'hour':
          const [year, month, day, hour] = timeKey.split('-');
          return `${day}/${month} ${hour}:00`;
        case 'minute':
          const [yearMin, monthMin, dayMin, hourMin, minuteMin] = timeKey.split('-');
          return `${dayMin}/${monthMin} ${hourMin}:${minuteMin}`;
        default:
          return timeKey;
      }
    },
    
    getStartTimeLabel(endLabel) {
      if (!endLabel) return '';
      
      try {
        // Parsear la etiqueta de fin (formato: "DD/MM HH:00" o "DD/MM HH:MM")
        // El punto en la gráfica representa el final del período
        const parts = endLabel.split(' ');
        if (parts.length < 2) return '';
        
        const datePart = parts[0]; // "DD/MM"
        const timePart = parts[1]; // "HH:00" o "HH:MM"
        
        const [day, month] = datePart.split('/');
        const [hour, minute] = timePart.split(':');
        
        // Obtener el año actual o del contexto del gráfico
        const currentYear = new Date().getFullYear();
        
        // Crear fecha de fin usando el año actual
        const endDate = new Date(currentYear, parseInt(month) - 1, parseInt(day), parseInt(hour), minute ? parseInt(minute) : 0, 0, 0);
        
        // Calcular fecha inicial restando el período seleccionado
        const startDate = new Date(endDate);
        if (this.selectedTimeUnit === 'hour') {
          startDate.setHours(startDate.getHours() - 1);
        } else {
          startDate.setMinutes(startDate.getMinutes() - 1);
        }
        
        // Formatear fecha inicial
        const startDay = String(startDate.getDate()).padStart(2, '0');
        const startMonth = String(startDate.getMonth() + 1).padStart(2, '0');
        const startHour = String(startDate.getHours()).padStart(2, '0');
        const startMinute = String(startDate.getMinutes()).padStart(2, '0');
        
        if (this.selectedTimeUnit === 'hour') {
          return `${startDay}/${startMonth} ${startHour}:00`;
        } else {
          return `${startDay}/${startMonth} ${startHour}:${startMinute}`;
        }
      } catch (error) {
        console.warn('Error calculating start time label:', error);
        return '';
      }
    },
    
    getTimeUnitLabel() {
      switch (this.selectedTimeUnit) {
        case 'hour': return this.$ml.get('hour');
        case 'minute': return this.$ml.get('minute');
        default: return this.$ml.get('time');
      }
    },
    
    getEventCount(eventTypeName) {
      const eventType = this.allEventTypes.find(et => et.name === eventTypeName);
      if (!eventType || !this.events) return 0;
      
      // Sumar los eventCount de los buckets en lugar de contar items
      return this.events
        .filter(event => this.getEventType(event) === eventType.key)
        .reduce((total, event) => {
          return total + this.getEventCountFromBucket(event);
        }, 0);
    },
    
    getEventCountFromBucket(event) {
      // Si es un bucket, devolver la cantidad de eventos dentro del bucket
      if (event.metadata && (event.metadata.isBucket || event.metadata.eventCount || event.metadata.cantidadEventos)) {
        return event.metadata.eventCount || event.metadata.cantidadEventos || 1;
      }
      // Si no es bucket, contar como 1 evento
      return 1;
    },
    
    getEventTypeDisplayName(eventType) {
      // Para objetos, mostrar solo el tipo sin el ID del bucket en la leyenda
      if (eventType.key && eventType.key.startsWith('object_')) {
        // Remover el ID del bucket si está presente (ej: "carrot (1769489952000)" -> "carrot")
        return eventType.name.replace(/\s*\(\d+\)$/, '');
      }
      return eventType.name;
    },
    
    distributeBucketEvents(event, eventType, eventCount, groupedData) {
      // Distribuir eventos proporcionalmente entre los períodos que el bucket abarca
      const start = new Date(event.start);
      const end = new Date(event.end);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime()) || start >= end) {
        // Si no hay fechas válidas, usar solo el start
        const timeKey = this.getTimeKey(event.start);
        if (timeKey) {
          groupedData[eventType][timeKey] = (groupedData[eventType][timeKey] || 0) + eventCount;
        }
        return;
      }
      
      // Obtener todos los períodos que el bucket abarca
      const periods = this.getPeriodsBetween(start, end);
      
      if (periods.length === 0) {
        // Si no hay períodos, usar solo el start
        const timeKey = this.getTimeKey(event.start);
        if (timeKey) {
          groupedData[eventType][timeKey] = (groupedData[eventType][timeKey] || 0) + eventCount;
        }
        return;
      }
      
      // Si el bucket está completamente dentro de un solo período, asignar todos los eventos a ese período
      if (periods.length === 1) {
        const timeKey = periods[0].timeKey;
        if (timeKey) {
          groupedData[eventType][timeKey] = (groupedData[eventType][timeKey] || 0) + eventCount;
        }
        return;
      }
      
      // Distribuir eventos proporcionalmente según la duración en cada período
      const totalDuration = end.getTime() - start.getTime();
      
      periods.forEach((period) => {
        const periodStart = new Date(period.start);
        const periodEnd = new Date(period.end);
        
        // Calcular la intersección del bucket con este período
        const intersectionStart = periodStart > start ? periodStart : start;
        const intersectionEnd = periodEnd < end ? periodEnd : end;
        
        if (intersectionStart < intersectionEnd) {
          const intersectionDuration = intersectionEnd.getTime() - intersectionStart.getTime();
          const proportion = intersectionDuration / totalDuration;
          const eventsInPeriod = Math.round(eventCount * proportion);
          
          // Asegurar que al menos se asigne 1 evento si hay intersección
          const finalCount = eventsInPeriod > 0 ? eventsInPeriod : (intersectionDuration > 0 ? 1 : 0);
          
          if (finalCount > 0 && period.timeKey) {
            groupedData[eventType][period.timeKey] = (groupedData[eventType][period.timeKey] || 0) + finalCount;
          }
        }
      });
    },
    
    getPeriodsBetween(start, end) {
      const periods = [];
      const current = new Date(start);
      
      // Normalizar al inicio del período actual según selectedTimeUnit
      if (this.selectedTimeUnit === 'hour') {
        current.setMinutes(0, 0, 0);
      } else {
        current.setSeconds(0, 0);
      }
      
      while (current < end) {
        const periodStart = new Date(current);
        let periodEnd;
        
        if (this.selectedTimeUnit === 'hour') {
          // Fin de la hora actual
          periodEnd = new Date(periodStart);
          periodEnd.setHours(periodEnd.getHours() + 1);
        } else {
          // Fin del minuto actual
          periodEnd = new Date(periodStart);
          periodEnd.setMinutes(periodEnd.getMinutes() + 1);
        }
        
        // Asegurar que no exceda el end del bucket
        if (periodEnd > end) {
          periodEnd = new Date(end);
        }
        
        const timeKey = this.getTimeKey(periodStart);
        if (timeKey) {
          periods.push({
            start: periodStart,
            end: periodEnd,
            timeKey: timeKey
          });
        }
        
        // Avanzar al siguiente período
        if (this.selectedTimeUnit === 'hour') {
          current.setHours(current.getHours() + 1);
        } else {
          current.setMinutes(current.getMinutes() + 1);
        }
      }
      
      return periods;
    },

    updateChartLabelsOnLanguageChange() {
      if (!this.chart) return;

      // Regenerar tipos de eventos con los nombres actualizados
      if (this.events && this.events.length > 0) {
        this.generateSmartEventTypes();
        this.generateAlertTypes();
        this.generateObjectTypes();
      }

      // Actualizar etiquetas del gráfico con el nuevo idioma
      this.chart.options.scales.xAxes[0].scaleLabel.labelString = this.getTimeUnitLabel();
      this.chart.options.scales.yAxes[0].scaleLabel.labelString = this.$ml.get('number_events');
      
      // Actualizar callbacks de tooltips
      this.chart.options.tooltips.callbacks.title = (tooltipItem, data) => {
        const label = data.labels[tooltipItem[0].index];
        // El punto representa el final del período, así que calcular el inicio
        const startLabel = this.getStartTimeLabel(label);
        return `${startLabel} - ${label}`;
      };
      this.chart.options.tooltips.callbacks.label = (tooltipItem, data) => {
        const eventType = this.allEventTypes[tooltipItem.datasetIndex];
        if (!eventType) {
          return `${this.$ml.get('event')}: ${tooltipItem.yLabel}`;
        }
        return `${eventType.name}, ${tooltipItem.yLabel}`;
      };

      // Regenerar los datasets con los nuevos nombres
      if (this.chart.data.datasets) {
        this.chart.data.datasets = this.allEventTypes.map(eventType => {
          const existingDataset = this.chart.data.datasets.find(ds => ds.label === eventType.name) ||
                                  this.chart.data.datasets[this.allEventTypes.indexOf(eventType)];
          
          return {
            label: eventType.name,
            data: existingDataset ? existingDataset.data : [],
            borderColor: eventType.color,
            backgroundColor: eventType.color + '20',
            tension: 0.4,
            fill: false,
            pointRadius: 4,
            pointHoverRadius: 6
          };
        });
      }

      // Forzar actualización del gráfico
      this.chart.update();
      
      // Forzar actualización de Vue para que reactive los computed properties
      this.$forceUpdate();
    }
  }
};
</script>

<style scoped>
.events-chart-container {
  padding: 16px;
  background: #2C2C2C;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  color: #ffffff;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.chart-controls {
  display: flex;
  align-items: center;
}

.chart-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 500;
  color: #ffffff;
}

.time-unit-select {
  max-width: 150px;
}

.chart-wrapper {
  position: relative;
  height: 200px;
  margin-bottom: 16px;
}

.chart-canvas {
  width: 100% !important;
  height: 100% !important;
}

.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.legend-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #ffffff;
}

.legend-count {
  font-size: 0.8rem;
  color: #cccccc;
}

.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 150px;
  color: #cccccc;
}
</style>
